import { useSignal } from "@preact/signals";
import { useCallback, useEffect } from "react";
import { getQueryParameter, setQueryParameter } from "../url";
/**
 * Like React.useState, but synchronises state with a URL query parameter and updates state from the
 * URL when the user navigates back/forward.
 *
 * @param param The name of the URL query parameter
 * @param decoder Function that takes the raw string from the query parameter and converts it to
 * the state's type. This function can be used to validate user input and provide a default value.
 * @param encoder Optional function that converts the state to a string. If this function is not
 * provided, JSON.stringify() will be used.
 */
export default function useQueryParamState(param, decoder, encoder) {
    const decodeValue = useCallback((value) => {
        if (decoder) {
            return decoder(value);
        }
        return value;
    }, [decoder]);
    const initialQueryParamValue = getQueryParameter(param);
    const initialParamState = decodeValue(initialQueryParamValue);
    const paramState = useSignal(initialParamState);
    const encodeValue = (value) => {
        if (encoder) {
            return encoder(value);
        }
        else if (typeof value === "object") {
            return JSON.stringify(value);
        }
        else if (typeof value === "number") {
            return String(value);
        }
        else if (typeof value === "string") {
            return value;
        }
        return "";
    };
    // Provide a mechanism to set the value that updates both the state and the query string
    const updateParamValue = (value, replaceUrl) => {
        paramState.value = value;
        setQueryParameter(param, encodeValue(value), replaceUrl);
    };
    useEffect(() => {
        if (typeof initialQueryParamValue === "undefined") {
            setQueryParameter(param, encodeValue(initialParamState), true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        // Listen to the popstate event and set state from the query string
        const onPopState = () => {
            paramState.value = decodeValue(getQueryParameter(param));
        };
        window.addEventListener("popstate", onPopState);
        return () => {
            // Remove the popstate listener when the component unmounts
            window.removeEventListener("popstate", onPopState);
        };
    }, [param, paramState, decodeValue]);
    return [paramState, updateParamValue];
}
